import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { ValidationService } from 'src/app/shared/services/validation.service';
import type {
  CompanyIntegration,
  CompanyIntegrationField,
} from 'src/app/settings/services/integrations/models/integrations.types';
import { MASKED_SECRET } from 'src/app/settings/services/integrations/models/masked-secret';

export { MASKED_SECRET } from 'src/app/settings/services/integrations/models/masked-secret';

export interface IntegrationCompanySettingsDialogData {
  integration: CompanyIntegration;
  values: Record<string, string>;
  /** When true, secret fields stay empty and editable (no mask / pencil). */
  add?: boolean;
}

export type IntegrationCompanySettingsDialogResult = Record<string, string>;

/**
 * Angular's `required` accepts a whitespace-only value; a credential made of
 * spaces would be stored and then fail against the provider.
 */
function nonBlank(control: AbstractControl): ValidationErrors | null {
  return control.value?.trim() ? null : { required: true };
}

@Component({
  selector: 'integration-company-settings-dialog',
  templateUrl: './integration-company-settings-dialog.component.html',
  styleUrl: './integration-company-settings-dialog.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
  ],
})
export class IntegrationCompanySettingsDialogComponent {
  readonly integration: CompanyIntegration;
  readonly fields: CompanyIntegrationField[];
  readonly form: FormGroup;

  private masked: Record<string, boolean> = {};

  constructor(
    public dialogRef: MatDialogRef<IntegrationCompanySettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: IntegrationCompanySettingsDialogData,
  ) {
    this.integration = data.integration;
    this.fields = data.integration.fields ?? [];

    const controls: Record<string, FormControl<string>> = {};

    for (const field of this.fields) {
      const stored = data.values?.[field.key] ?? '';
      // Add flow: secrets are always blank + editable. Manage flow: mask stored secrets.
      const masked = !data.add && !!field.secret && !!stored;
      const initial = masked
        ? MASKED_SECRET
        : data.add && field.secret
          ? ''
          : stored;

      this.masked[field.key] = masked;
      controls[field.key] = new FormControl(initial, {
        nonNullable: true,
        validators: this.validatorsFor(field, masked),
      });
    }

    this.form = new FormGroup(controls);
  }

  get canSubmit(): boolean {
    return this.form.valid;
  }

  control(field: CompanyIntegrationField): FormControl<string> {
    return this.form.get(field.key) as FormControl<string>;
  }

  isMasked(field: CompanyIntegrationField): boolean {
    return !!this.masked[field.key];
  }

  edit(field: CompanyIntegrationField): void {
    this.masked[field.key] = false;

    const control = this.control(field);

    control.setValue('');
    control.setValidators(this.validatorsFor(field, false));
    control.updateValueAndValidity();
  }

  /**
   * A `url` field renders as text so the browser does not demand a scheme;
   * the previous company settings form accepted a bare host too.
   */
  inputType(field: CompanyIntegrationField): string {
    return field.type === 'email' ? 'email' : 'text';
  }

  hasError(field: CompanyIntegrationField): boolean {
    const control = this.control(field);

    return control.invalid && (control.dirty || control.touched);
  }

  errorId(field: CompanyIntegrationField): string {
    return `integrationFieldError_${field.key}`;
  }

  errorMessage(field: CompanyIntegrationField): string | null {
    if (!this.hasError(field)) {
      return null;
    }

    const control = this.control(field);

    if (control.hasError('required')) {
      return `${field.label} is required.`;
    }

    if (control.hasError('pattern')) {
      return field.type === 'email'
        ? 'Enter a valid email address.'
        : 'Enter a valid address, such as company.example.com.';
    }

    return null;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const result: IntegrationCompanySettingsDialogResult = {};

    for (const field of this.fields) {
      if (this.isMasked(field)) {
        continue;
      }
      result[field.key] = this.control(field).value.trim();
    }

    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private validatorsFor(
    field: CompanyIntegrationField,
    masked: boolean,
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [nonBlank];

    if (masked) {
      return validators;
    }

    if (field.type === 'email') {
      validators.push(Validators.pattern(ValidationService.EMAIL_REGEX));
    } else if (field.type === 'url') {
      validators.push(Validators.pattern(ValidationService.WEBSITE_REGEX));
    }

    return validators;
  }
}
